'use client'
import { useState } from 'react'
import styled from 'styled-components'
import LoaderWrapper from '@src/quince-components/src/atoms/loaderWrapper/LoaderWrapper'
import { getlbUserId } from '@src/quince-site-common/src/utils/getlb-user-id'
import AuthWrapper from '../../components/Auth/AuthWrapper'
import FormHeader from '../../components/Auth/FormHeader'
import { TELEMETRY_EVENT_NAMES } from '@src/telemetry/telemetry-events.constants'
import { trackEvent } from '@src/telemetry/track'
import { captureException } from '../../components/Sentry/utils'
import useLoginUser from '../../hooks/useLoginUser'
import styles from '../LogIn/Components/loginComponents.module.scss'
import { SignUpVariantA } from '../SignUp/Variants/ExperimentA'
import validateEmail from '@src/quince-site-common/src/utils/validateEmail'
import mixpanelCl from '@src/telemetry/integrations/mixpanel'
import { isEmpty } from '@src/utils/functions/utils'
import { useLoginSideEffectsAndRedirect } from './hooks/use-login-sideeffects'
import SignupOTPModal from '@src/pages/sign-up/sign-up-otp-modal/SignupOTPModal'
import { useTranslation } from 'next-i18next'
import { getLocalisationDataFromStorage } from '@src/utils/general.utils'
import { signUp as signUpUniversal, confirmSignUp, resendSignUpCode } from '@lastbrand/fe-common/universal-auth'

const StyledSignUp = styled.section``

const SignUp = ({ data }) => {
  const { t } = useTranslation('common')
  const { t: tAccount } = useTranslation('accountPage')
  const page = data.authenticationPage
  const { loginUser } = useLoginUser()
  const [errors, setErrors] = useState()

  const [stage, setStage] = useState(0)
  const [serverError, setServerError] = useState(null)
  const [email, setEmail] = useState(null)
  const [loading, setLoading] = useState(false)
  const [passwordError, setPasswordError] = useState()
  const loginDownStreamSideEffectsWithRedirect = useLoginSideEffectsAndRedirect(setServerError)
  const [isOtpModalOpen, setIsOtpModalOpen] = useState(false)
  const [resendLoading, setResendLoading] = useState(false)
  const [verifyLoading, setVerifyLoading] = useState(false)
  const [password, setPassword] = useState('')

  const getSignUpParams = (username, password, name, family_name) => {
    const shouldIncludeRegionId =
      typeof window !== 'undefined' && window.__debug?.isI18nEnabled && getLocalisationDataFromStorage()?.regionId

    const shouldIncludeLocale =
      typeof window !== 'undefined' && window.__debug?.isI18nEnabled && getLocalisationDataFromStorage()?.locale

    return {
      username: username?.toLowerCase(),
      password,
      clientMetadata: {
        ...(shouldIncludeLocale && {
          locale: getLocalisationDataFromStorage()?.locale?.toString(),
        }),
      },
      attributes: {
        name,
        family_name,
        email: username,
        ...(shouldIncludeRegionId && {
          'custom:region_id': getLocalisationDataFromStorage()?.regionId?.toString(),
        }),
      },
    }
  }

  const signUp = async (nameAndFamilyName, username, password, isPasswordValid) => {
    setErrors({})

    const error = {}
    const [name, family_name] = (nameAndFamilyName || '').split(' ')

    if (!nameAndFamilyName) {
      error.name = { message: t('full_name_required') }
    } else if (!family_name) {
      error.name = { message: t('please_fill_in_the_last_name') }
    }

    if (!username) {
      error.username = { message: t('please_fill_in_the_email') }
    } else if (!validateEmail(username)) {
      error.username = { message: t('please_enter_valid_email') }
    }
    if (!password) {
      error.password = { message: t('please_fill_in_the_password') }
    }

    if (!isEmpty(error) || !isPasswordValid) {
      setErrors(error)
      setPasswordError(true)
      return
    }

    if (username) {
      username = username?.toLowerCase()
    }
    setEmail(username)
    setPassword(password)

    try {
      setLoading(true)

      const signUpParams = getSignUpParams(username, password, name, family_name)

      const signUpResponse = await signUpUniversal(
        username?.toLowerCase(),
        password,
        name,
        family_name,
        signUpParams.clientMetadata,
        signUpParams.attributes,
      )

      trackEvent(TELEMETRY_EVENT_NAMES.FORM_SUBMIT, {
        trackInternal: true,
        // @i18n-ignore
        category: 'login',
        action: 'account_create',
        context: {
          login_email: username,
          lb_user_id: getlbUserId(),
          // @i18n-ignore
          signup_method: 'general',
        },
      })

      if (!signUpResponse.isSignUpComplete) {
        setIsOtpModalOpen(true)
        setLoading(false)
        return
      }

      setStage(1)
      await loginUser(username, password)
      await loginDownStreamSideEffectsWithRedirect()
      mixpanelCl.identify({ email: username, isTentativeEmail: false })

      setLoading(false)
    } catch (err) {
      setLoading(false)
      if (err && err.name == 'InvalidParameterException') {
        if (err.message && err.message.indexOf('password') > -1) {
          error.password = {
            message: t('password_min_length_requirement'),
          }
        }
      }
      if (err && err.name == 'InvalidPasswordException') {
        if (err.message && err.message.indexOf('not long') > -1) {
          error.password = {
            message: t('password_min_length_requirement'),
          }
        }
      }
      if (err && err.name == 'UsernameExistsException') {
        err.message = `${tAccount('account_already_exists')}<br/><a href="/log-in">${tAccount('please_log_in')}</a>`
      }
      if (!isEmpty(error)) {
        setErrors(error)
      } else {
        setServerError(err)
      }

      const errorCodesToIgnore = new Set([
        'UsernameExistsException',
        'InvalidPasswordException',
        'InvalidParameterException',
      ])
      /** No need of capturing above error events because they are not genuine issues */
      if (!errorCodesToIgnore.has(err.code)) {
        captureException(
          err,
          { username },
          {
            type: 'API_FAILURE',
            // @i18n-ignore
            API: 'SIGNUP',
          },
        )
      }
    }
  }

  const verifyOtp = async otp => {
    try {
      setVerifyLoading(true)
      const confirmSignUpResponse = await confirmSignUp(email, otp)

      if (confirmSignUpResponse.isSignUpComplete) {
        setIsOtpModalOpen(false)
        setStage(1)

        await loginUser(email, password)
        await loginDownStreamSideEffectsWithRedirect()
        mixpanelCl.identify({ email, isTentativeEmail: false })
      }
    } catch (err) {
      if (err && err.name === 'CodeMismatchException') {
        setServerError(tAccount('incorrect_verification_code'))
      } else if (err && err.name === 'ExpiredCodeException') {
        setServerError(tAccount('verification_code_expired'))
      } else {
        setServerError(tAccount('account_verification_failed'))
      }
      captureException(err, { email }, { type: 'API_FAILURE', API: 'CONFIRM_SIGNUP' })
    } finally {
      setVerifyLoading(false)
    }
  }

  const resendOtp = async () => {
    try {
      const shouldIncludeLocale =
        typeof window !== 'undefined' && window.__debug?.isI18nEnabled && getLocalisationDataFromStorage()?.locale
      setResendLoading(true)
      await resendSignUpCode(email, {
        ...(shouldIncludeLocale ? { locale: getLocalisationDataFromStorage()?.locale?.toString() } : {}),
      })
      setResendLoading(false)
    } catch (err) {
      setResendLoading(false)
      setServerError(t('error_resending_otp_please_try_again'))
      captureException(err, { email }, { type: 'API_FAILURE', API: 'RESEND_SIGNUP_CODE' })
    }
  }

  return (
    <StyledSignUp>
      <AuthWrapper image={page.image}>
        {stage === 0 && (
          <SignUpVariantA
            serverError={serverError}
            email={email}
            errors={errors}
            loading={loading}
            signUp={signUp}
            passwordError={passwordError}
          />
        )}
        {stage === 1 && (
          <FormHeader className={styles.signUpLoading}>
            <div data-testid="signup-complete-loading-header" className={styles.signUpLoadingHeader}>
              {t('welcome_to_quince')}
            </div>
            <div className={styles.signUpLoadingSubHeader}>{t('account_creation_success')}</div>
            <LoaderWrapper loadWrapperClassName={styles.pageLoader} showLoader />
          </FormHeader>
        )}
      </AuthWrapper>
      <SignupOTPModal
        open={isOtpModalOpen}
        onClose={() => setIsOtpModalOpen(false)}
        email={email || ''}
        loading={verifyLoading}
        resendLoading={resendLoading}
        onSubmit={verifyOtp}
        onResend={resendOtp}
        error={serverError}
      />
    </StyledSignUp>
  )
}

export default SignUp
